/**
 * Phase 0.5 Component G — /admin/access page.
 *
 * One screen for the policy administrator: enter a user id, see their
 * assignments, grant new ones, revoke, or fire a break-glass.
 *
 * Component F's primitives (`<ActionGate action="grant_access" …>`)
 * would normally guard the buttons; we defer that to Component H once
 * the resolver populates `principal.attr.scope_path` end-to-end. For
 * now the page itself is mounted behind the existing admin role check
 * at the route level (see App.tsx).
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Key, Network, ShieldAlert, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { ResolvedId, useAdminNames } from '../nameResolution';
import { useListUsers } from '../People/hooks';
import { BreakGlassRequestDialog } from './BreakGlassRequestDialog';
import { CreateScopeNodeDialog } from './CreateScopeNodeDialog';
import { DelegateMyAccessPanel } from './DelegateMyAccessPanel';
import { GrantAssignmentDialog } from './GrantAssignmentDialog';
import { isScheduledAssignment, useAccessRequests, useListAssignmentsForUser } from './hooks';
import { RevokeConfirmDialog } from './RevokeConfirmDialog';
import { ScopeTreePanel } from './ScopeTreePanel';
// Matches RequireAdminRoute.tsx/AppSidebar.tsx/DashboardLayout.tsx's own
// ADMIN_ROLES — keep in sync.
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
function formatTimestamp(ts) {
    if (!ts || Number(ts.seconds) === 0) return '—';
    const date = new Date(Number(ts.seconds) * 1000);
    return date.toLocaleString();
}
export function AssignmentsListPage() {
    // The server's reviewer gate (an active access_grantors member, or
    // admin/internal ONLY while grantors are unconfigured — see
    // access_requests.go::isReviewer) doubles as the client-side signal for
    // which view of /admin/access to render (D12): the power tools below for
    // grantors/admins, or the owner-centric self-service panel for everyone
    // else. Fail closed to the self-service view while the check is in
    // flight or errors — a reviewer may see one extra render flash of the
    // self-service panel, but a non-reviewer must never be parked on the
    // admin shell (see security-review note, D12 slice 2).
    //
    // isReviewer alone under-covers real admins once a tenant configures
    // grantors: an admin who isn't also an active grantor gets reviewer:false
    // and would otherwise be dropped onto the self-service panel — the same
    // gap access_history.go's explicit isAdmin-OR-isReviewer already closes
    // server-side. OR in the frontend's own admin-role signal so a genuine
    // admin always reaches the console; every RPC the console calls
    // (AdminListGroups, AdminListBundles, AdminGetScopeTree, ListUsers) stays
    // independently admin-gated server-side, so this stays fail-closed for
    // anyone who ISN'T really an admin.
    const { user } = useAuth();
    const isFrontendAdmin = !!user?.role && ADMIN_ROLES.has(user.role.toLowerCase());
    const { data: reviewerCheck } = useAccessRequests('');
    const isReviewerOrAdmin = reviewerCheck?.reviewer === true || isFrontendAdmin;
    const [draftUserId, setDraftUserId] = useState('');
    const [activeUserId, setActiveUserId] = useState('');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [grantOpen, setGrantOpen] = useState(false);
    const [breakGlassOpen, setBreakGlassOpen] = useState(false);
    const [scopeNodeOpen, setScopeNodeOpen] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);
    // WS5 "Delegate…": grant dialog prefilled as a designee of this row.
    const [delegateFrom, setDelegateFrom] = useState(null);
    const { data: assignments, isLoading, error, refetch: refetchAssignments } = useListAssignmentsForUser(activeUserId, {
        includeInactive,
        enabled: !!activeUserId
    });
    // Both hooks below fan out to admin-only RPCs (AdminListGroups,
    // AdminListBundles, AdminGetScopeTree, ListUsers) — gate them on the
    // reviewer/admin determination so a non-reviewer's render doesn't fire
    // calls the server is about to refuse anyway (see isReviewerOrAdmin above).
    const names = useAdminNames(activeUserId ? [
        activeUserId
    ] : [], {
        enabled: isReviewerOrAdmin
    });
    // Wave 5 typeahead source: skip the search when the draft looks like a raw
    // pangaea id (32-36 hex/dash chars) — that's the manual-paste fallback.
    const looksLikeId = /^[0-9a-f-]{32,36}$/i.test(draftUserId);
    const { data: userSearch } = useListUsers(looksLikeId ? '' : draftUserId, 1, 8, {
        enabled: isReviewerOrAdmin
    });
    if (!isReviewerOrAdmin) {
        return /*#__PURE__*/ _jsx(DelegateMyAccessPanel, {});
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "container mx-auto p-6 space-y-6",
        "data-testid": Testids.adminAccess.page,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-2xl font-semibold",
                                children: "Access administration"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground text-sm",
                                children: "Manage role-bundle assignments, designees, and break-glass grants."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                "data-testid": Testids.adminAccess.scopeNodeButton,
                                onClick: ()=>setScopeNodeOpen(true),
                                children: [
                                    /*#__PURE__*/ _jsx(Network, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    "New scope node"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                "data-testid": Testids.adminAccess.breakGlassButton,
                                onClick: ()=>setBreakGlassOpen(true),
                                children: [
                                    /*#__PURE__*/ _jsx(ShieldAlert, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    "Break-glass"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                "data-testid": Testids.adminAccess.grantButton,
                                onClick: ()=>setGrantOpen(true),
                                children: [
                                    /*#__PURE__*/ _jsx(UserPlus, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    "Grant role"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(CardHeader, {
                        children: /*#__PURE__*/ _jsx(CardTitle, {
                            children: "Lookup a user"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(CardContent, {
                        className: "space-y-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-end gap-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "relative flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "user-id",
                                                children: "User"
                                            }),
                                            /*#__PURE__*/ _jsx(Input, {
                                                id: "user-id",
                                                "data-testid": Testids.adminAccess.userIdInput,
                                                value: draftUserId,
                                                onChange: (e)=>setDraftUserId(e.target.value),
                                                placeholder: "search by name or email (or paste a user id)"
                                            }),
                                            draftUserId && !looksLikeId && (userSearch?.users?.length ?? 0) > 0 && /*#__PURE__*/ _jsx("div", {
                                                className: "bg-background absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-md border shadow-sm",
                                                children: (userSearch?.users ?? []).map((u)=>/*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        "data-testid": Testids.adminAccess.userOption,
                                                        "data-user-id": u.id,
                                                        className: "hover:bg-muted block w-full px-3 py-2 text-left",
                                                        onClick: ()=>{
                                                            setDraftUserId(u.id);
                                                            setActiveUserId(u.id);
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                children: u.name || u.email
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-muted-foreground text-xs",
                                                                children: u.email
                                                            })
                                                        ]
                                                    }, u.id))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        "data-testid": Testids.adminAccess.listButton,
                                        onClick: ()=>{
                                            // Same user as already listed: the query key won't change, so
                                            // React Query won't refetch on its own — force it. This is
                                            // the only way to observe server-driven state changes (e.g.
                                            // the reaper expiring a delegation) without switching users.
                                            if (draftUserId === activeUserId) {
                                                void refetchAssignments();
                                            } else {
                                                setActiveUserId(draftUserId);
                                            }
                                        },
                                        disabled: !draftUserId,
                                        children: "List assignments"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Checkbox, {
                                        id: "include-inactive",
                                        "data-testid": Testids.adminAccess.includeInactiveToggle,
                                        checked: includeInactive,
                                        onCheckedChange: (checked)=>setIncludeInactive(checked === true)
                                    }),
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "include-inactive",
                                        className: "font-normal",
                                        children: "Include revoked / expired"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            activeUserId && /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(CardHeader, {
                        children: /*#__PURE__*/ _jsxs(CardTitle, {
                            children: [
                                "Assignments for ",
                                /*#__PURE__*/ _jsx(ResolvedId, {
                                    id: activeUserId,
                                    name: names.user(activeUserId)
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(CardContent, {
                        children: [
                            isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Loading…"
                            }),
                            error && /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive text-sm",
                                children: error instanceof Error ? error.message : 'Failed to load'
                            }),
                            assignments && assignments.length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                "data-testid": Testids.adminAccess.emptyState,
                                children: "No assignments for this user."
                            }),
                            assignments && assignments.length > 0 && /*#__PURE__*/ _jsxs(Table, {
                                children: [
                                    /*#__PURE__*/ _jsx(TableHeader, {
                                        children: /*#__PURE__*/ _jsxs(TableRow, {
                                            children: [
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Bundle"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Scope"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Type"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Window"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Status"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    className: "text-right",
                                                    children: "Actions"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TableBody, {
                                        children: assignments.map((a)=>{
                                            const isRevoked = !!a.revokedAt && Number(a.revokedAt.seconds) !== 0;
                                            const isScheduled = isScheduledAssignment(a);
                                            return /*#__PURE__*/ _jsxs(TableRow, {
                                                "data-testid": Testids.adminAccess.assignmentRow,
                                                "data-assignment-id": a.id,
                                                children: [
                                                    /*#__PURE__*/ _jsxs(TableCell, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "font-medium",
                                                                children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                                    id: a.roleBundleKey,
                                                                    name: names.bundle(a.roleBundleKey)
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-muted-foreground text-xs",
                                                                children: a.roleBundleKey
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(TableCell, {
                                                        children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: a.scopeNodeId,
                                                            name: names.scope(a.scopeNodeId)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(TableCell, {
                                                        children: /*#__PURE__*/ _jsx(Badge, {
                                                            variant: a.assignmentType === 'break_glass' ? 'destructive' : 'secondary',
                                                            children: a.assignmentType
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs(TableCell, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs",
                                                                children: formatTimestamp(a.effectiveStart)
                                                            }),
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "text-muted-foreground text-xs",
                                                                children: [
                                                                    "→ ",
                                                                    formatTimestamp(a.effectiveEnd)
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(TableCell, {
                                                        children: [
                                                            isRevoked ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Badge, {
                                                                        variant: "outline",
                                                                        children: "Revoked"
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "text-muted-foreground mt-1 text-xs",
                                                                        "data-testid": Testids.adminAccess.assignmentRevokedReason,
                                                                        children: [
                                                                            formatTimestamp(a.revokedAt),
                                                                            a.revokedReason ? ` — ${a.revokedReason}` : ''
                                                                        ]
                                                                    })
                                                                ]
                                                            }) : isScheduled ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "secondary",
                                                                "data-testid": Testids.adminAccess.assignmentScheduledBadge,
                                                                children: "Scheduled"
                                                            }) : /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "default",
                                                                children: "Active"
                                                            }),
                                                            a.delegationId && /*#__PURE__*/ _jsx("div", {
                                                                className: "mt-1",
                                                                children: /*#__PURE__*/ _jsxs(Badge, {
                                                                    variant: "outline",
                                                                    className: "text-muted-foreground text-[10px]",
                                                                    "data-testid": Testids.adminAccess.assignmentDelegationStatusBadge,
                                                                    children: [
                                                                        "Delegation: ",
                                                                        a.delegationStatus || 'unknown'
                                                                    ]
                                                                })
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(TableCell, {
                                                        className: "text-right",
                                                        children: !isRevoked && /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-end gap-1.5",
                                                            children: [
                                                                a.userId && a.assignmentType !== 'designee' && /*#__PURE__*/ _jsx(Button, {
                                                                    variant: "ghost",
                                                                    size: "sm",
                                                                    "data-testid": Testids.adminAccess.delegateButton,
                                                                    disabled: isScheduled,
                                                                    title: isScheduled ? 'This grant is not active yet — a scheduled grant cannot be delegated until it starts' : 'Time-boxed designee grant of this role at this scope',
                                                                    onClick: ()=>setDelegateFrom({
                                                                            ownerUserId: a.userId,
                                                                            roleBundleKey: a.roleBundleKey,
                                                                            scopeNodeId: a.scopeNodeId
                                                                        }),
                                                                    children: "Delegate…"
                                                                }),
                                                                /*#__PURE__*/ _jsxs(Button, {
                                                                    variant: "outline",
                                                                    size: "sm",
                                                                    "data-testid": Testids.adminAccess.revokeButton,
                                                                    onClick: ()=>setRevokeTarget({
                                                                            id: a.id,
                                                                            userId: a.userId,
                                                                            bundleDisplayName: names.bundle(a.roleBundleKey) || a.roleBundleDisplayName
                                                                        }),
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Key, {
                                                                            className: "mr-1 h-3 w-3"
                                                                        }),
                                                                        "Revoke"
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    })
                                                ]
                                            }, a.id);
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ScopeTreePanel, {}),
            /*#__PURE__*/ _jsx(GrantAssignmentDialog, {
                open: grantOpen || !!delegateFrom,
                onOpenChange: (o)=>{
                    setGrantOpen(o);
                    if (!o) setDelegateFrom(null);
                },
                defaultUserId: activeUserId,
                delegateFrom: delegateFrom
            }),
            /*#__PURE__*/ _jsx(CreateScopeNodeDialog, {
                open: scopeNodeOpen,
                onOpenChange: setScopeNodeOpen
            }),
            /*#__PURE__*/ _jsx(BreakGlassRequestDialog, {
                open: breakGlassOpen,
                onOpenChange: setBreakGlassOpen,
                defaultUserId: activeUserId
            }),
            revokeTarget && /*#__PURE__*/ _jsx(RevokeConfirmDialog, {
                open: true,
                onOpenChange: (o)=>!o && setRevokeTarget(null),
                assignmentId: revokeTarget.id,
                userId: revokeTarget.userId,
                bundleDisplayName: revokeTarget.bundleDisplayName
            })
        ]
    });
}
